import type { UseQueryResult } from '@tanstack/react-query';
import { Eye, MessageSquare, TrendingUp, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { getLocale, type Locale } from '../../../i18n';
import { Absent } from '../../../chrome/measure';
import { IconHint } from '../../../chrome/hint';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import { ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import type { Localities, LocalityRow } from '../api/schemas';
import { deltaOf, fractionsOf } from '../overview/figures';
import { tt } from './messages';

/** The rail's three readings of one locality: its reach, its reach ranked by
 *  movement, and its comment volume. */
export const RAIL_METRICS = ['reach', 'trend', 'comments'] as const;
export type RailMetric = (typeof RAIL_METRICS)[number];

const ICONS: Record<RailMetric, LucideIcon> = {
  reach: Eye,
  trend: TrendingUp,
  comments: MessageSquare,
};

const ROWS_SHOWN = 6;

export function figureOf(
  row: LocalityRow,
  metric: RailMetric,
): { value: number | null; previous: number | null } {
  return metric === 'comments' ? row.comment_volume : row.reach;
}

/** `168 mil`, `4,2 mil`: the frame's figure treatment. */
export function compact(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 })
    .format(value)
    .replace(/\u00a0/g, ' ');
}

const CHIP_TONE = { up: 'positive', down: 'negative', flat: 'neutral' } as const;

function DeltaChip(props: { figure: { value: number | null; previous: number | null } }) {
  const delta = deltaOf(props.figure, 'percent', getLocale());
  if (delta === null) return null;
  return (
    <Chip tone={CHIP_TONE[delta.trend]} size="sm" data-trend={delta.trend}>
      {delta.text}
      <span className="sr-only"> {tt('delta.against')}</span>
    </Chip>
  );
}

function Rows(props: { rows: readonly LocalityRow[]; metric: RailMetric }): ReactNode {
  const rows = props.rows.slice(0, ROWS_SHOWN);
  const fractions = fractionsOf(rows.map((row) => figureOf(row, props.metric).value));
  const locale = getLocale();
  return (
    <ul className="flex flex-col">
      {rows.map((row, index) => {
        const figure = figureOf(row, props.metric);
        const fraction = fractions[index];
        return (
          <li
            key={row.place_id}
            className="border-border flex flex-col gap-2 border-t py-3 first:border-t-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-body flex-1 truncate">{row.name}</span>
              <span className="text-body" data-numeric>
                {figure.value === null ? (
                  <Absent reason="not_measured" />
                ) : (
                  compact(figure.value, locale)
                )}
              </span>
              <DeltaChip figure={figure} />
            </div>
            <div aria-hidden className="bg-secondary rounded-pill h-1.5 overflow-hidden">
              {fraction !== undefined && (
                <div
                  data-slot="bar"
                  className="bg-primary rounded-pill h-full"
                  style={{ width: `${fraction * 100}%` }}
                />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** The route's localities in the order it ranked them; the toggle picks the
 *  figure each row shows and the order the route is asked for. */
export function NeighbourhoodsCard(props: {
  query: UseQueryResult<Localities, ApiFailureError>;
  metric: RailMetric;
  onMetricChange: (metric: RailMetric) => void;
}): ReactNode {
  const level = props.query.data?.level ?? 'neighborhood';
  return (
    <Card className="gap-0">
      <CardHeader className="flex items-center justify-between gap-2">
        <h2 className="text-title-sm">{tt(`localities.title.${level}`)}</h2>
        <ToggleGroup
          type="single"
          spacing={1}
          size="sm"
          aria-label={tt('localities.metric.label')}
          value={props.metric}
          onValueChange={(next) => {
            if (next !== '') props.onMetricChange(next as RailMetric);
          }}
          className="bg-secondary rounded-pill p-1"
        >
          {RAIL_METRICS.map((metric) => {
            const Icon = ICONS[metric];
            return (
              <IconHint key={metric} label={tt(`localities.metric.${metric}`)}>
                <ToggleGroupItem value={metric} className="rounded-pill data-[state=on]:bg-card">
                  <Icon aria-hidden />
                </ToggleGroupItem>
              </IconHint>
            );
          })}
        </ToggleGroup>
      </CardHeader>
      <CardContent>
        <QueryStates
          query={props.query}
          isEmpty={(data) => data.localities.length === 0}
          empty={<EmptyState title={tt('localities.empty')} />}
        >
          {(data) => <Rows rows={data.localities} metric={props.metric} />}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
